import type { QuietPeriod } from '../../core/types';

export function parseClock(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (match === null) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function validQuietPeriod(period: Pick<QuietPeriod, 'start' | 'end'>): boolean {
  const start = parseClock(period.start);
  const end = parseClock(period.end);
  return start !== null && end !== null && start !== end;
}

function minuteOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function inQuietPeriod(date: Date, periods: readonly QuietPeriod[]): boolean {
  const current = minuteOfDay(date);
  return periods.some((period) => {
    const start = parseClock(period.start);
    const end = parseClock(period.end);
    if (start === null || end === null || start === end) return false;
    return start < end
      ? current >= start && current < end
      : current >= start || current < end;
  });
}

/** First minute after every overlapping quiet period has ended. */
export function shiftOutOfQuietPeriod(date: Date, periods: readonly QuietPeriod[]): Date {
  let candidate = new Date(date);
  for (let attempt = 0; attempt < periods.length + 1; attempt += 1) {
    if (!inQuietPeriod(candidate, periods)) return candidate;
    let furthestEnd = -1;
    for (const period of periods) {
      const start = parseClock(period.start);
      const end = parseClock(period.end);
      if (start === null || end === null || start === end) continue;
      const current = minuteOfDay(candidate);
      const active = start < end ? current >= start && current < end : current >= start || current < end;
      if (!active) continue;
      furthestEnd = Math.max(furthestEnd, end);
    }
    if (furthestEnd < 0) return candidate;
    candidate = new Date(candidate);
    candidate.setHours(0, furthestEnd + 1, 0, 0);
    if (furthestEnd < minuteOfDay(date)) candidate.setDate(candidate.getDate() + 1);
  }
  return candidate;
}
